import React, { useMemo, useState } from 'react';
import { RequireOrg, VpState } from '../components/VelopulseShell';
import { vpApi, errorMessage, fmtMoney } from '../api/velopulse';

const todayIso = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const EMPTY = {
  acquisition_date: '2018-03-01',
  transfer_date: todayIso(),
  sale_price: '15000000',
  cost: '10000000',
  expenses: '500000',
  self_use: false,
};

const wan = (n) => {
  if (n == null || n === '') return '—';
  const v = Number(n) / 10000;
  const shown = Number.isInteger(v) ? v.toLocaleString('en-US') : v.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return `${shown} 萬`;
};

function Breakdown({ result }) {
  const b = result.breakdown || {};
  const rows = [
    ['成交價', fmtMoney(b.sale_price), wan(b.sale_price)],
    ['取得成本', fmtMoney(b.cost), wan(b.cost)],
    ['必要費用', fmtMoney(b.expenses), wan(b.expenses)],
    ['交易所得', fmtMoney(b.profit), wan(b.profit)],
  ];
  if (result.self_use_applied) {
    rows.push(['自住免稅額', fmtMoney(b.exemption), wan(b.exemption)]);
  }
  rows.push(['課稅所得', fmtMoney(b.taxable_income), wan(b.taxable_income)]);
  rows.push(['適用稅率', result.rate_label, `${result.rate_percent}%`]);
  rows.push(['應納稅額', fmtMoney(b.tax_amount), wan(b.tax_amount)]);

  return (
    <table className="vp-tax-table">
      <tbody>
        {rows.map(([k, v, w]) => (
          <tr key={k}>
            <th>{k}</th>
            <td>{v}</td>
            <td className="vp-muted">{w}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RealtyTax() {
  const [form, setForm] = useState(EMPTY);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const yearsHint = useMemo(() => {
    if (!form.acquisition_date || !form.transfer_date) return '';
    const a = new Date(`${form.acquisition_date}T00:00:00Z`);
    const b = new Date(`${form.transfer_date}T00:00:00Z`);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b < a) return '';
    const days = Math.round((b - a) / 86400000);
    const years = Math.round((days / 365) * 1000) / 1000;
    return `目前持有約 ${years} 年（${days.toLocaleString('en-US')} 日）`;
  }, [form.acquisition_date, form.transfer_date]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setResult(null);
    try {
      const data = await vpApi.estimateRealtyTax({
        acquisition_date: form.acquisition_date,
        transfer_date: form.transfer_date,
        sale_price: form.sale_price,
        cost: form.cost,
        expenses: form.expenses,
        self_use: form.self_use,
      });
      setResult(data);
    } catch (err) {
      setError(errorMessage(err, '試算失敗，請檢查欄位後再試。'));
    } finally { setSaving(false); }
  };

  return (
    <div className="vp-content vp-tax">
      <div className="vp-hello">
        <div>
          <h1>房地合一稅試算</h1>
          <p>依房地合一 2.0 個人稅率級距估算：未滿 2 年 45%、2–5 年 35%、5–10 年 20%、10 年以上 15%；符合自住要件且持有滿 6 年可適用 400 萬免稅與 10% 優惠。</p>
        </div>
      </div>

      <div className="vp-tax-layout">
        <form className="vp-card" onSubmit={submit}>
          <h2>交易資料</h2>
          <div className="vp-row2">
            <div className="vp-field">
              <label htmlFor="acq">取得日</label>
              <input id="acq" className="vp-input" type="date" required value={form.acquisition_date} onChange={set('acquisition_date')} />
            </div>
            <div className="vp-field">
              <label htmlFor="xfer">移轉日</label>
              <input id="xfer" className="vp-input" type="date" required value={form.transfer_date} onChange={set('transfer_date')} />
            </div>
          </div>
          {yearsHint && <p className="vp-hint" style={{ marginTop: 4 }}>{yearsHint}</p>}
          <div className="vp-field">
            <label htmlFor="sale">成交價（元）</label>
            <input id="sale" className="vp-input" type="number" min="0" step="1" required value={form.sale_price} onChange={set('sale_price')} placeholder="例如 15000000" />
          </div>
          <div className="vp-field">
            <label htmlFor="cost">取得成本（元）</label>
            <input id="cost" className="vp-input" type="number" min="0" step="1" required value={form.cost} onChange={set('cost')} placeholder="例如 10000000" />
          </div>
          <div className="vp-field">
            <label htmlFor="exp">必要費用（元）</label>
            <input id="exp" className="vp-input" type="number" min="0" step="1" value={form.expenses} onChange={set('expenses')} placeholder="仲介費、登記費等，沒有可填 0" />
          </div>
          <label className="vp-check">
            <input type="checkbox" checked={form.self_use} onChange={set('self_use')} />
            <span>
              符合自住要件
              <small>本人、配偶或未成年子女設籍並居住連續滿 6 年，持有期間無出租或營業使用。未滿 6 年即使勾選也不適用優惠。</small>
            </span>
          </label>
          {error && <div className="vp-error" role="alert">{error}</div>}
          <div className="vp-wiz-actions">
            <button type="button" className="vp-btn lg" onClick={() => { setForm(EMPTY); setResult(null); setError(''); }}>還原範例</button>
            <button type="submit" className="vp-btn lg primary" disabled={saving}>{saving ? '試算中…' : '開始試算'}</button>
          </div>
          <p className="vp-disclaimer" role="note">試算僅供參考，非正式核定</p>
        </form>

        <div className="vp-card">
          <h2>試算結果</h2>
          {!result && !error && <VpState text="填寫左側資料後按「開始試算」。土地漲價總數額、重購退稅與非居住者稅率尚未納入。" />}
          {result && (
            <div className="vp-tax-result">
              <div className="vp-tax-hero">
                <div className="l">應納稅額</div>
                <div className="v">{fmtMoney(result.tax_amount)}</div>
                <div className="w">{wan(result.tax_amount)}</div>
              </div>
              <div className="vp-tax-kpis">
                <div><div className="l">適用稅率</div><div className="v">{result.rate_label}</div></div>
                <div><div className="l">持有期間</div><div className="v">{result.years_held} 年</div><div className="vp-muted">{result.days_held?.toLocaleString('en-US')} 日 · {result.hold_band_label}</div></div>
              </div>
              {result.self_use_applied && <div className="vp-okbox">已套用自住優惠：所得 400 萬以內免稅，超出部分 10%。</div>}
              <Breakdown result={result} />
              {!!result.notes?.length && (
                <ul className="vp-tax-notes">
                  {result.notes.map((n) => <li key={n}>{n}</li>)}
                </ul>
              )}
              <p className="vp-disclaimer" role="note">{result.disclaimer || '試算僅供參考，非正式核定'}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function RealtyTaxPage() {
  return <RequireOrg><RealtyTax /></RequireOrg>;
}
